import type { AppView } from '@aegislab/shared';
import type { AppNavSectionId } from '@/shared/config';
import { palette, rgba } from '@aegislab/design-tokens';
import { brightTokens } from '@/shared/ui/tokens';

export interface DockTone {
  accent: string;
  accentSoft: string;
  glow: string;
  tint: string;
  border: string;
}

const tone = {
  time: {
    accent: brightTokens.secondary.default,
    accentSoft: brightTokens.secondary.light,
    glow: brightTokens.secondary.glow,
    tint: brightTokens.secondary.faint,
    border: rgba(palette.cyan, 0.24),
  },
  journal: {
    accent: brightTokens.status.pending,
    accentSoft: 'rgba(139, 92, 246, 0.18)',
    glow: 'rgba(139, 92, 246, 0.34)',
    tint: 'rgba(139, 92, 246, 0.08)',
    border: 'rgba(139, 92, 246, 0.24)',
  },
  chat: {
    accent: brightTokens.status.success,
    accentSoft: brightTokens.status.successFaint,
    glow: brightTokens.status.successGlow,
    tint: 'rgba(34, 197, 94, 0.08)',
    border: 'rgba(34, 197, 94, 0.24)',
  },
  reward: {
    accent: brightTokens.rarity.legendary,
    accentSoft: brightTokens.medal.gold.faint,
    glow: 'rgba(212, 168, 78, 0.34)',
    tint: 'rgba(212, 168, 78, 0.08)',
    border: 'rgba(212, 168, 78, 0.24)',
  },
  team: {
    accent: brightTokens.primary.default,
    accentSoft: brightTokens.badge.primary.background,
    glow: brightTokens.primary.glow,
    tint: rgba(palette.blue, 0.08),
    border: brightTokens.badge.primary.border,
  },
  ops: {
    accent: brightTokens.status.warning,
    accentSoft: 'rgba(245, 158, 11, 0.16)',
    glow: 'rgba(245, 158, 11, 0.32)',
    tint: 'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.24)',
  },
  people: {
    accent: '#FB7185',
    accentSoft: 'rgba(251, 113, 133, 0.16)',
    glow: 'rgba(251, 113, 133, 0.32)',
    tint: 'rgba(251, 113, 133, 0.08)',
    border: 'rgba(251, 113, 133, 0.24)',
  },
  finance: {
    accent: '#34D399',
    accentSoft: 'rgba(52, 211, 153, 0.16)',
    glow: 'rgba(52, 211, 153, 0.34)',
    tint: 'rgba(52, 211, 153, 0.08)',
    border: 'rgba(52, 211, 153, 0.24)',
  },
  system: {
    accent: brightTokens.status.info,
    accentSoft: 'rgba(59, 130, 246, 0.16)',
    glow: 'rgba(59, 130, 246, 0.32)',
    tint: 'rgba(59, 130, 246, 0.08)',
    border: 'rgba(59, 130, 246, 0.24)',
  },
} as const;

export const VIEW_TONES: Record<AppView, DockTone> = {
  today: tone.journal,
  quests: tone.reward,
  'world-quests': tone.reward,
  'dev-quests': tone.reward,
  attendance: tone.time,
  'work-log': tone.journal,
  chat: tone.chat,
  leaderboard: tone.reward,
  'team-settings': tone.team,
  'admin-attendance': tone.ops,
  'hr-management': tone.people,
  finance: tone.finance,
  // 정산 실행은 돈이 나가는 표면이라 재무와 같은 모티프 가족이다.
  'campaign-settlement': tone.finance,
  'domestic-settlement': tone.finance,
  'bug-reports': tone.system,
  // 읽고 적는 글 — 오늘 일지와 같은 기록 가족이다.
  wiki: tone.journal,
  'system-admin': tone.system,
  scenarios: tone.ops,
  'advertiser-console': tone.ops,
  'creator-relations': tone.people,
  'creator-lists': tone.people,
  'ceo-console': tone.reward,
  // 대표 콘솔과 같은 표를 보는 창이라 같은 가족이다.
  'usage-dashboard': tone.reward,
  // 받는 몫을 보는 창이라 리더보드와 같은 보상 가족이다.
  'campaign-bonus': tone.reward,
  // PR1 placeholder tone — 라운지 모티프 PB0 라운드에서 확정 시 갱신
  // [Source: project_design_domain_motifs — 라운지 = Retro-Futuristic, PB0 detail TBD]
  lounge: tone.journal,
};

const DEFAULT_TONE: DockTone = tone.system;

export function getDockTone(view: AppView): DockTone {
  switch (view) {
    case 'attendance':
      return tone.time;
    case 'work-log':
    case 'wiki':
      return tone.journal;
    case 'chat':
      return tone.chat;
    case 'leaderboard':
    case 'quests':
    case 'world-quests':
    case 'dev-quests':
    case 'ceo-console':
    case 'usage-dashboard':
    case 'campaign-bonus':
      return tone.reward;
    case 'team-settings':
      return tone.team;
    case 'admin-attendance':
      return tone.ops;
    case 'hr-management':
    case 'creator-relations':
    case 'creator-lists':
      return tone.people;
    case 'finance':
    case 'campaign-settlement':
    case 'domestic-settlement':
      return tone.finance;
    case 'bug-reports':
    case 'system-admin':
      return tone.system;
    // 아래 뷰들은 기존에 default 로 떨어져 DEFAULT_TONE(=tone.system)을 받았다.
    // 동작 보존을 위해 동일한 DEFAULT_TONE 을 반환하는 명시 case 로 전환한다.
    case 'today':
    case 'scenarios':
    case 'advertiser-console':
    case 'lounge':
      return DEFAULT_TONE;
    default:
      return DEFAULT_TONE;
  }
}

export const SECTION_TONES: Record<AppNavSectionId, DockTone> = {
  self: tone.time,
  team: tone.team,
  organization: tone.people,
  platform: tone.system,
} as const;

export function getDockSectionTone(sectionId: AppNavSectionId): DockTone {
  return SECTION_TONES[sectionId] ?? DEFAULT_TONE;
}
